/**
 * Optimización de imágenes.
 *
 * Los originales que bajamos de Instagram son enormes (2304x3072, 300-620 KB
 * cada uno) y se pintan como mucho a ~600 px, así que servirlos tal cual era
 * tirar varios megas por post.
 *
 * Los redimensionamos con images.weserv.nl, que ya es una dependencia de la
 * web (contentModels.ts lo usa para las imágenes legacy del blog y para las
 * del CDN de Instagram, que bloquea el hotlinking). Es gratuito, cachea un año
 * en su propio CDN y de paso nos ahorra egress de Supabase.
 *
 * NO usamos /storage/v1/render/image/ de Supabase: hace lo mismo pero se
 * factura por "origin image" y este catálogo son ~1.100 imágenes.
 */

const STORAGE_PUBLIC_SEGMENT = "/storage/v1/object/public/";
const WESERV_HOST = "images.weserv.nl";

const isTransformableImage = (path: string): boolean => {
  const clean = path.split("?")[0].toLowerCase();
  return [".jpg", ".jpeg", ".png", ".webp", ".avif"].some((ext) => clean.endsWith(ext));
};

export type ImageSizeOptions = {
  /** Ancho máximo en píxeles del archivo que pedimos. */
  width: number;
  quality?: number;
};

/**
 * Monta la URL del proxy. `fit=inside` conserva la proporción y `we` evita que
 * agrande una imagen que ya sea más pequeña que el ancho pedido.
 */
const viaWeserv = (upstream: string, { width, quality }: Required<ImageSizeOptions>): string =>
  `https://${WESERV_HOST}/?url=${encodeURIComponent(upstream)}` +
  `&w=${Math.round(width)}&q=${quality}&output=webp&fit=inside&we`;

/**
 * Devuelve la URL optimizada equivalente. Si no es una imagen que sepamos
 * redimensionar (vídeo, ruta relativa, dominio externo, data:) la devuelve
 * intacta.
 */
export function optimizedImageUrl(
  url: string | null | undefined,
  { width, quality = 70 }: ImageSizeOptions,
): string {
  if (!url) return "";
  if (url.startsWith("data:")) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Rutas relativas propias (/logo-640.webp): salen tal cual de /public, sin
    // pasar por ningun optimizador --la de Vercel es de Next, no de Vite--, asi
    // que lo que se suba ahi es lo que se descarga. Subelas ya a su tamano.
    return url;
  }

  // Imágenes que ya pasan por el proxy: solo hay que añadirle el resize.
  if (parsed.hostname === WESERV_HOST) {
    const upstream = parsed.searchParams.get("url");
    if (!upstream || !isTransformableImage(upstream)) return url;
    return viaWeserv(upstream, { width, quality });
  }

  // Nuestras propias imágenes en Supabase Storage.
  if (parsed.pathname.includes(STORAGE_PUBLIC_SEGMENT)) {
    if (!isTransformableImage(parsed.pathname)) return url;
    return viaWeserv(`${parsed.host}${parsed.pathname}${parsed.search}`, { width, quality });
  }

  return url;
}

/**
 * Atajo para miniaturas de tarjetas (home, listados, portadas del feed).
 * Se pintan a ~300-400 px, así que pedir el original de 2304 px era tirar
 * medio mega por tarjeta.
 */
export function thumbnailImageUrl(url: string | null | undefined): string {
  return optimizedImageUrl(url, { width: 640, quality: 65 });
}

/** Ancho al que pedimos las fotos del carrusel (el mismo que sirve Instagram). */
export const GALLERY_WIDTH = 1080;
export const GALLERY_QUALITY = 68;

/**
 * Atajo para las fotos grandes del carrusel. Vive aquí, y no en el componente,
 * porque el build necesita montar exactamente la misma URL para adelantarla
 * (ver primeraFotoDelBuild.ts): dos copias de las medidas se separan solas.
 */
export function galleryImageUrl(url: string | null | undefined): string {
  return optimizedImageUrl(url, { width: GALLERY_WIDTH, quality: GALLERY_QUALITY });
}
